import { useContext } from 'react';
import { ERROR_MESSAGES } from '../constants';
import { DashboardContext } from '../dashboard-context';
import { DashboardContextValue } from '../types';

export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext);

  if (context === undefined) {
    throw new Error(ERROR_MESSAGES.CONTEXT_NOT_FOUND);
  }

  return context;
};

export const useDashboardData = () => {
  const {
    account,
    accountBalance,
    program,
    statistics,
    profitableDays,
    assetType,
  } = useDashboard();
  return {
    account,
    accountBalance,
    program,
    statistics,
    profitableDays,
    assetType,
  };
};

/**
 * Hook to get dashboard loading state
 * Useful for conditional rendering based on loading state
 *
 * @returns boolean indicating if any dashboard data is loading
 */
export const useDashboardLoading = () => {
  const { isLoading } = useDashboard();
  return isLoading;
};

/**
 * Hook to get dashboard error state
 * Useful for error handling and display
 *
 * @returns Error message from any context or null
 */
export const useDashboardError = () => {
  const { error } = useDashboard();
  return error;
};

/**
 * Hook to get dashboard actions
 * Useful when component needs to trigger data updates
 *
 * @returns Object with all dashboard actions
 */
export const useDashboardActions = () => {
  const {
    refreshAll,
    retry,
    updateAccount,
    updateBalance,
    updateProgram,
    recalculateStatistics,
  } = useDashboard();

  return {
    refreshAll,
    retry,
    updateAccount,
    updateBalance,
    updateProgram,
    recalculateStatistics,
  };
};

// Convenience hooks for specific data (backward compatibility)
export const useAccount = () => {
  const { account } = useDashboard();
  return account;
};

export const useAccountBalance = () => {
  const { accountBalance } = useDashboard();
  return accountBalance;
};

export const useProgram = () => {
  const { program } = useDashboard();
  return program;
};

export const useStatistics = () => {
  const { statistics } = useDashboard();
  return statistics;
};
